<template>
  <div class="admin-container">
    <AdminSidebar :isOpen="isSidebarOpen" :userData="currentUser" />

    <!-- Main Content -->
    <div
      class="main-content"
      :class="{
        'content-shifted': isSidebarOpen
      }"
    >
      <nav class="admin-nav">
        <button class="menu-btn" @click="toggleSidebar">
          <i class="fas fa-bars"></i>
        </button>
      </nav>


      <div v-if="!isAdmin && !isAccountant" class="admin-content" style="display: flex; justify-content: center; align-items: center; min-height: 50vh;">
        <div style="text-align: center;">
          <i class="fas fa-ban" style="font-size: 3rem; color: #e53e3e; margin-bottom: 1rem;"></i>
          <h2 style="font-size: 1.5rem; margin-bottom: 0.5rem; color: #1a202c;">Truy cập bị từ chối</h2>
          <p style="color: #4a5568;">Tài khoản nhân viên (staff) không có quyền xem trang hệ thống thống kê Dashboard.</p>
        </div>
      </div>
      <div v-else class="admin-content">
        <!-- Consistent booking table for both AdminDashboard and AdminBooking -->
        <div class="data-section">
          <div class="section-header">
            <h2>{{ isDashboard ? 'Đặt Vé Gần Đây' : 'Quản Lý Đặt Vé' }}</h2>
          </div>

          <div class="stats-grid">
            <SpotlightCard spotlight-color="rgba(72, 187, 120, 0.2)">
              <div class="stat-card" data-type="bookings">
                <div class="stat-card-content">
                  <div class="stat-icon-wrapper">
                    <i class="fas fa-ticket-alt stat-icon"></i>
                  </div>
                  <div class="stat-details">
                    <h3>Tổng Số Vé</h3>
                    <p class="stat-value">
                      <CountUp :to="stats.totalBookings || 0" :duration="2" separator="." />
                    </p>
                    <div class="stat-trend">
                      <span
                        class="trend-indicator"
                        :class="{
                          'trend-up': stats.bookingTrend > 0,
                          'trend-down': stats.bookingTrend < 0,
                          'trend-neutral': stats.bookingTrend === 0
                        }"
                      >
                        <i
                          :class="
                            stats.bookingTrend > 0
                              ? 'fas fa-arrow-up'
                              : stats.bookingTrend < 0
                              ? 'fas fa-arrow-down'
                              : 'fas fa-minus'
                          "
                        ></i>
                        {{ Math.abs(stats.bookingTrend || 0) }}%
                      </span>
                      <small>so với tháng trước</small>
                    </div>
                  </div>
                </div>
              </div>
            </SpotlightCard>

            <SpotlightCard spotlight-color="rgba(66, 153, 225, 0.2)">
              <div class="stat-card" data-type="trips">
                <div class="stat-card-content">
                  <div class="stat-icon-wrapper">
                    <i class="fas fa-bus stat-icon"></i>
                  </div>
                  <div class="stat-details">
                    <h3>Chuyến Đang Chạy</h3>
                    <p class="stat-value">
                      <CountUp :to="stats.activeTrips || 0" :duration="2" separator="." />
                    </p>
                    <div class="stat-trend">
                      <span
                        class="trend-indicator"
                        :class="{
                          'trend-up': stats.tripTrend > 0,
                          'trend-down': stats.tripTrend < 0,
                          'trend-neutral': stats.tripTrend === 0
                        }"
                      >
                        <i
                          :class="
                            stats.tripTrend > 0
                              ? 'fas fa-arrow-up'
                              : stats.tripTrend < 0
                              ? 'fas fa-arrow-down'
                              : 'fas fa-minus'
                          "
                        ></i>
                        {{ Math.abs(stats.tripTrend || 0) }}%
                      </span>
                      <small>so với tuần trước</small>
                    </div>
                  </div>
                </div>
              </div>
            </SpotlightCard>

            <SpotlightCard spotlight-color="rgba(237, 137, 54, 0.2)">
              <div class="stat-card" data-type="revenue">
                <div class="stat-card-content">
                  <div class="stat-icon-wrapper">
                    <i class="fas fa-money-bill-wave stat-icon"></i>
                  </div>
                  <div class="stat-details">
                    <h3>Doanh Thu</h3>
                    <p class="stat-value">
                      {{ formatCurrency(stats.totalRevenue || 0) }}
                    </p>
                    <div class="stat-trend">
                      <span
                        class="trend-indicator"
                        :class="{
                          'trend-up': stats.revenueTrend > 0,
                          'trend-down': stats.revenueTrend < 0,
                          'trend-neutral': stats.revenueTrend === 0
                        }"
                      >
                        <i
                          :class="
                            stats.revenueTrend > 0
                              ? 'fas fa-arrow-up'
                              : stats.revenueTrend < 0
                              ? 'fas fa-arrow-down'
                              : 'fas fa-minus'
                          "
                        ></i>
                        {{ Math.abs(stats.revenueTrend || 0) }}%
                      </span>
                      <small>so với tháng trước</small>
                    </div>
                  </div>
                </div>
              </div>
            </SpotlightCard>
          </div>

          <FadeContent :duration="800" :translateY="30">
          <div class="charts-container">
            <div class="chart-filters">
              <h3>Biểu đồ thống kê</h3>
              <div class="chart-period-controls">
                <div class="period-selector">
                  <label for="chart-period">Hiển thị theo:</label>
                  <select
                    id="chart-period"
                    v-model="chartPeriod"
                    @change="updateCharts"
                  >
                    <option value="day">Ngày</option>
                    <option value="week">Tuần</option>
                    <option value="month">Tháng</option>
                    <option value="year">Năm</option>
                  </select>
                </div>

                <div class="year-selector" v-if="chartPeriod !== 'day'">
                  <label for="chart-year">Năm:</label>
                  <select
                    id="chart-year"
                    v-model="chartYear"
                    @change="updateCharts"
                  >
                    <option
                      v-for="year in availableYears"
                      :key="year"
                      :value="year"
                    >
                      {{ year }}
                    </option>
                  </select>
                </div>

                <div
                  class="month-selector"
                  v-if="chartPeriod === 'day' || chartPeriod === 'week'"
                >
                  <label for="chart-month">Tháng:</label>
                  <select
                    id="chart-month"
                    v-model="chartMonth"
                    @change="updateCharts"
                  >
                    <option v-for="month in 12" :key="month" :value="month">
                      Tháng {{ month }}
                    </option>
                  </select>
                </div>
              </div>
            </div>

            <div class="charts-grid" v-if="isDataReady">
              <div class="chart-card">
                <h4>Doanh thu</h4>
                <div class="chart-container">
                  <canvas ref="revenueChart"></canvas>
                </div>
              </div>

              <div class="chart-card">
                <h4>Đặt vé</h4>
                <div class="chart-container">
                  <canvas ref="bookingsChart"></canvas>
                </div>
              </div>

              <div class="chart-card wide">
                <h4>Phân bổ theo tuyến đường</h4>
                <div class="chart-container">
                  <canvas ref="routeDistributionChart"></canvas>
                </div>
              </div>

              <div class="chart-card">
                <h4>Trạng thái vé</h4>
                <div class="chart-container">
                  <canvas ref="bookingStatusChart"></canvas>
                </div>
              </div>

              <div class="chart-card">
                <h4>Loại vé</h4>
                <div class="chart-container">
                  <canvas ref="ticketTypeChart"></canvas>
                </div>
              </div>
            </div>
          </div>
          </FadeContent>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axiosInstance from '@/plugins/axios'
import AdminSidebar from '../common/AdminSidebar.vue'
import SpotlightCard from '@/components/ui/SpotlightCard.vue'
import CountUp from '@/components/ui/CountUp.vue'
import FadeContent from '@/components/ui/FadeContent.vue'
import Chart from 'chart.js/auto'
import { ToastService } from '@/services/ToastService'
const isProd = process.env.VUE_APP_IS_PROD === 'true'
const API_BASE_URL = isProd
  ? process.env.VUE_APP_BACKEND_URL_PROD
  : process.env.VUE_APP_BACKEND_URL_DEV

export default {
  name: 'AdminDashboard',
  components: {
    AdminSidebar,
    SpotlightCard,
    CountUp,
    FadeContent
  },
  data () {
    return {
      isAdmin: true,
      isAccountant: false,
      isDashboardUnlocked: true,

      isDataReady: false,
      isDashboard: true,
      stats: {
        totalBookings: 0,
        activeTrips: 0,
        totalRevenue: 0,
        bookingTrend: 5, // Add these trend properties
        tripTrend: -2,
        revenueTrend: 8
      },
      bookings: [],
      pickupLocations: [],
      isSidebarOpen: false,
      selectedBooking: null,
      currentUser: {
        full_name: 'Admin User',
        role: 'Administrator'
      },
      dateRange: {
        from: null,
        to: null
      },
      charts: {
        revenue: null,
        bookings: null,
        routeDistribution: null,
        bookingStatus: null,
        ticketType: null
      },
      chartData: {
        revenue: [],
        bookings: [],
        routes: {},
        statuses: {
          pending: 0,
          confirmed: 0,
          paid: 0,
          canceled: 0,
          paying: 0,
          refund: 0,
          exchange: 0,
          reserved: 0
        },
        ticketTypes: {}
      },
      chartPeriod: 'month',
      chartYear: new Date().getFullYear(),
      chartMonth: new Date().getMonth() + 1
    }
  },
  computed: {
    userInitials () {
      return this.currentUser.full_name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
    },
    availableYears () {
      const currentYear = new Date().getFullYear()
      return Array.from({ length: 5 }, (_, i) => currentYear - i)
    }
  },
  methods: {

    formatCurrency (amount) {
      if (!amount) return 'N/A'
      return new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND'
      }).format(amount)
    },
    getVehicleTypeName (vehicleType) {
      const vehicleTypes = {
        '16_slots': 'Xe 16 chỗ',
        '28_slots': 'Xe 28 chỗ',
        '45_slots': 'Xe 45 chỗ',
        over_night: 'Xe giường nằm'
      }
      return vehicleTypes[vehicleType] || vehicleType
    },
    checkRole () {
      try {
        const token = localStorage.getItem('adminToken')
        if (!token) return
        const payload = JSON.parse(atob(token.split('.')[1]))
        this.isAdmin = payload.role === 'admin'
        this.isAccountant = payload.role === 'accountant'
      } catch (e) {
        this.isAdmin = false
        this.isAccountant = false
      }
    },
    async fetchDashboardData () {
      try {
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        let fromDate = null
        let toDate = null

        if (this.dateRange.from !== null && this.dateRange.to !== null) {
          fromDate = new Date(this.dateRange.from)
          fromDate.setHours(0, 0, 0, 0)
          toDate = new Date(this.dateRange.to)
          toDate.setHours(23, 59, 59, 999)
        }

        const statsRes = await axiosInstance.get(
          `${API_BASE_URL}/admin/stats`,
          {
            headers,
            params: {
              from_date: fromDate,
              to_date: toDate
            }
          }
        )

        this.stats = statsRes.data
      } catch (error) {
        console.error('Error fetching dashboard data:', error)
        if (error.response?.status === 401) {
          this.handleLogout()
        }
      }
    },

    fetchData () {
      this.fetchDashboardData()
      this.fetchChartData()
    },

    translateStatus (status) {
      const statusMap = {
        pending: 'Chờ xác nhận',
        confirmed: 'Đã xác nhận',
        paid: 'Đã thanh toán',
        paying: 'Đang thanh toán',
        canceled: 'Đã hủy',
        refund: 'Hoàn tiền',
        exchange: 'Đổi vé',
        reserved: 'Bảo lưu'
      }

      return statusMap[status.toLowerCase()] || status
    },
    handleLogout () {
      localStorage.removeItem('adminToken')
      localStorage.removeItem('refreshToken')
      this.$router.push('/login')
    },
    formatDate (date) {
      if (!date) return '-'
      try {
        return new Date(date).toLocaleDateString('vi-VN', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (error) {
        console.error('Date formatting error:', error)
        return '-'
      }
    },
    formatDateForInput (date) {
      if (!date) return ''
      const d = new Date(date)
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${d.getFullYear()}-${month}-${day}`
    },
    toggleSidebar () {
      this.isSidebarOpen = !this.isSidebarOpen
      localStorage.setItem('sidebarOpen', this.isSidebarOpen)
    },
    async openBookingModal (booking) {
      try {
        this.selectedBooking = {
          ...booking,
          // Ensure pickupLocationId is properly set as a number or null
          pickupLocationId: booking.pickupLocationId
            ? Number(booking.pickupLocationId)
            : null
        }

        if (this.selectedBooking.ticketTypeId) {
          await this.fetchTicketTypeDetails(this.selectedBooking.ticketTypeId)
        }

        if (
          this.selectedBooking.pickupLocationId &&
          !this.pickupLocations.some(
            loc =>
              loc.pickup_location_id === this.selectedBooking.pickupLocationId
          )
        ) {
          this.selectedBooking.pickupLocationId = ''
        }
      } catch (error) {
        console.error('Error opening booking modal:', error)
        ToastService.error('Có lỗi khi mở thông tin đặt vé. Vui lòng thử lại.')
      }
    },

    async fetchTicketTypeDetails (ticketTypeId) {
      try {
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        const response = await axiosInstance.get(
          `${API_BASE_URL}/ticket_types/${ticketTypeId}`,
          {
            headers
          }
        )

        // Update the selected booking with ticket details
        this.selectedBooking.ticketTypeName = response.data.name
        this.selectedBooking.ticketPrice = response.data.price
      } catch (error) {
        console.error('Error fetching ticket type details:', error)
      }
    },
    closeModal () {
      this.selectedBooking = null
    },
    async updateBookingStatus () {
      try {
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        await axiosInstance.put(
          `${API_BASE_URL}/admin/bookings/${this.selectedBooking.id}`,
          {
            status: this.selectedBooking.status,
            customer_name: this.selectedBooking.customerName,
            customer_phone: this.selectedBooking.customerPhone,
            customer_email: this.selectedBooking.customerEmail,
            birth_year: this.selectedBooking.birthYear,
            pickup_location_id: this.selectedBooking.pickupLocationId
          },
          { headers }
        )

        // Show success message
        ToastService.success('Cập nhật thành công')
        this.closeModal()

        // Refresh data
        this.fetchData()
      } catch (error) {
        console.error('Error updating booking:', error)
        const errorMsg =
          error.response?.data?.detail ||
          error.message ||
          'Có lỗi khi cập nhật. Vui lòng thử lại.'
        ToastService.error(errorMsg)
      }
    },

    async fetchChartData () {
      try {
        this.isDataReady = false

        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        const response = await axiosInstance.get(
          `${API_BASE_URL}/admin/chart-data`,
          {
            headers,
            params: {
              period: this.chartPeriod,
              year: this.chartYear,
              month: this.chartMonth
            }
          }
        )

        if (!this._isMounted) return

        const data = response.data

        // Đảm bảo tất cả các thuộc tính cần thiết đều tồn tại
        this.chartData = {
          revenue: Array.isArray(data.revenue) ? data.revenue : [],
          bookings: Array.isArray(data.bookings) ? data.bookings : [],
          routes: data.routes || {},
          statuses: data.statuses || {},
          ticketTypes: data.ticketTypes || {}
        }

        // Đặt flag để cho biết dữ liệu đã sẵn sàng
        this.isDataReady = true

        // Sử dụng nextTick để đảm bảo DOM đã được cập nhật trước khi vẽ biểu đồ
        this.$nextTick(() => {
          if (this._isMounted) {
            this.renderCharts()
          }
        })
      } catch (error) {
        console.error('Lỗi khi tải dữ liệu biểu đồ:', error)
        ToastService.error('Có lỗi khi tải dữ liệu biểu đồ. Vui lòng thử lại.')
      }
    },

    updateCharts () {
      this.fetchChartData()
    },

    // Hủy biểu đồ cũ trước khi tạo mới
    destroyCharts () {
      try {
        const charts = Object.values(this.charts).filter(
          chart => chart !== null
        )

        // Use Promise.all to ensure all charts are properly destroyed
        Promise.all(
          charts.map(chart => {
            return new Promise(resolve => {
              try {
                // Cancel any pending animations
                if (chart.animating) {
                  chart.stop()
                }
                chart.destroy()
                resolve()
              } catch (e) {
                console.warn('Error destroying chart:', e)
                resolve()
              }
            })
          })
        ).then(() => {
          // Reset all chart references
          Object.keys(this.charts).forEach(key => {
            this.charts[key] = null
          })
        })
      } catch (error) {
        console.error('Error in destroyCharts:', error)
      }
    },

    // Hiển thị biểu đồ
    renderCharts () {
      try {
        this.destroyCharts()

        if (!this._isMounted) return

        // Kiểm tra tất cả các canvas elements có tồn tại không
        const missingElements = []

        if (!this.$refs.revenueChart) missingElements.push('revenueChart')
        if (!this.$refs.bookingsChart) missingElements.push('bookingsChart')
        if (!this.$refs.routeDistributionChart)
          missingElements.push('routeDistributionChart')
        if (!this.$refs.bookingStatusChart)
          missingElements.push('bookingStatusChart')
        if (!this.$refs.ticketTypeChart) missingElements.push('ticketTypeChart')

        if (missingElements.length > 0) {
          ToastService.warning(
            `Canvas của các biểu đồ không tồn tại: ${missingElements.join(
              ', '
            )}`
          )
          return
        }

        // 1. Biểu đồ doanh thu (Line chart)
        this.charts.revenue = new Chart(this.$refs.revenueChart, {
          type: 'line',
          data: {
            labels: this.chartData.revenue.map(item => item.label),
            datasets: [
              {
                label: 'Doanh thu (VNĐ)',
                data: this.chartData.revenue.map(item => item.value),
                backgroundColor: 'rgba(54, 162, 235, 0.2)',
                borderColor: 'rgba(54, 162, 235, 1)',
                borderWidth: 2,
                tension: 0.3,
                fill: false // Tắt fill để tránh lỗi
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: false
              },
              tooltip: {
                callbacks: {
                  label: function (context) {
                    return new Intl.NumberFormat('vi-VN', {
                      style: 'currency',
                      currency: 'VND'
                    }).format(context.raw)
                  }
                }
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  callback: function (value) {
                    return value.toLocaleString('vi-VN') + ' ₫'
                  }
                }
              }
            }
          }
        })

        // 2. Biểu đồ đặt vé (Bar chart) - SỬA CHỖ NÀY
        this.charts.bookings = new Chart(this.$refs.bookingsChart, {
          type: 'bar',
          data: {
            labels: this.chartData.bookings.map(item => item.label),
            datasets: [
              {
                label: 'Số lượt đặt vé',
                data: this.chartData.bookings.map(item => item.value),
                backgroundColor: 'rgba(75, 192, 192, 0.6)',
                borderColor: 'rgba(75, 192, 192, 1)',
                borderWidth: 1
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: false
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  precision: 0
                }
              }
            }
          }
        })

        // Biểu đồ phân bổ tuyến đường (Horizontal bar chart)
        const routeLabels = Object.keys(this.chartData.routes)
        const routeData = Object.values(this.chartData.routes)

        this.charts.routeDistribution = new Chart(
          this.$refs.routeDistributionChart,
          {
            type: 'bar',
            data: {
              labels: routeLabels,
              datasets: [
                {
                  label: 'Số vé',
                  data: routeData,
                  backgroundColor: [
                    'rgba(255, 99, 132, 0.7)',
                    'rgba(54, 162, 235, 0.7)',
                    'rgba(255, 206, 86, 0.7)',
                    'rgba(75, 192, 192, 0.7)',
                    'rgba(153, 102, 255, 0.7)',
                    'rgba(255, 159, 64, 0.7)'
                  ],
                  borderColor: [
                    'rgba(255, 99, 132, 1)',
                    'rgba(54, 162, 235, 1)',
                    'rgba(255, 206, 86, 1)',
                    'rgba(75, 192, 192, 1)',
                    'rgba(153, 102, 255, 1)',
                    'rgba(255, 159, 64, 1)'
                  ],
                  borderWidth: 1
                }
              ]
            },
            options: {
              indexAxis: 'y',
              responsive: true,
              maintainAspectRatio: false,
              scales: {
                x: {
                  beginAtZero: true,
                  ticks: {
                    precision: 0
                  }
                }
              }
            }
          }
        )

        // Biểu đồ trạng thái đặt vé (Doughnut chart)
        const statusLabels = Object.keys(this.chartData.statuses)
        const statusData = Object.values(this.chartData.statuses)
        const statusColors = {
          pending: 'rgba(255, 206, 86, 0.7)',
          confirmed: 'rgba(54, 162, 235, 0.7)',
          paid: 'rgba(75, 192, 192, 0.7)',
          canceled: 'rgba(255, 99, 132, 0.7)',
          paying: 'rgba(153, 102, 255, 0.7)',
          refund: 'rgba(190, 24, 93, 0.7)',
          exchange: 'rgba(91, 33, 182, 0.7)',
          reserved: 'rgba(3, 105, 161, 0.7)'
        }

        this.charts.bookingStatus = new Chart(this.$refs.bookingStatusChart, {
          type: 'doughnut',
          data: {
            labels: statusLabels.map(status => this.translateStatus(status)),
            datasets: [
              {
                data: statusData,
                backgroundColor: statusLabels.map(
                  status => statusColors[status] || 'rgba(201, 203, 207, 0.7)'
                ),
                borderColor: statusLabels.map(
                  status =>
                    statusColors[status]?.replace('0.7', '1') ||
                    'rgba(201, 203, 207, 1)'
                ),
                borderWidth: 1
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom'
              }
            }
          }
        })

        // Biểu đồ loại vé (Pie chart)
        const typeLabels = Object.keys(this.chartData.ticketTypes)
        const typeData = Object.values(this.chartData.ticketTypes)

        this.charts.ticketType = new Chart(this.$refs.ticketTypeChart, {
          type: 'pie',
          data: {
            labels: typeLabels,
            datasets: [
              {
                data: typeData,
                backgroundColor: [
                  'rgba(255, 159, 64, 0.7)',
                  'rgba(255, 99, 132, 0.7)',
                  'rgba(54, 162, 235, 0.7)',
                  'rgba(75, 192, 192, 0.7)',
                  'rgba(153, 102, 255, 0.7)',
                  'rgba(255, 206, 86, 0.7)'
                ],
                borderColor: [
                  'rgba(255, 159, 64, 1)',
                  'rgba(255, 99, 132, 1)',
                  'rgba(54, 162, 235, 1)',
                  'rgba(75, 192, 192, 1)',
                  'rgba(153, 102, 255, 1)',
                  'rgba(255, 206, 86, 1)'
                ],
                borderWidth: 1
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom'
              }
            }
          }
        })
      } catch (error) {
        console.error('Error rendering charts:', error)
      }
    }
  },
  mounted () {
    this._isMounted = true
    this.checkRole()
    const sidebarState = localStorage.getItem('sidebarOpen')
    if (sidebarState !== null) this.isSidebarOpen = sidebarState === 'true'
    // Fetch data on load
    setTimeout(() => {
      this.fetchDashboardData()
      this.fetchChartData()
    }, 200)
  },

  beforeUnmount () {
    this._isMounted = false
    this.destroyCharts()
  },
  beforeRouteLeave (to, from, next) {
    // Clean up before navigation
    this._isMounted = false
    this.destroyCharts()
    next()
  }
}
</script>

<style scoped>
@import '@/assets/styles/admin.scss';

.charts-container {
  margin-top: 2rem;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  padding: 1.5rem;
}

.chart-filters {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;

  h3 {
    font-size: 1.25rem;
    margin: 0;
    color: #2d3748;
  }

  .chart-period-controls {
    display: flex;
    gap: 1rem;

    .period-selector,
    .year-selector,
    .month-selector {
      display: flex;
      align-items: center;

      label {
        margin-right: 0.5rem;
        font-size: 0.9rem;
        color: #4a5568;
      }

      select {
        padding: 0.35rem 0.75rem;
        border: 1px solid #e2e8f0;
        border-radius: 4px;
        background-color: #f7fafc;
        font-size: 0.9rem;
        color: #2d3748;
        cursor: pointer;

        &:focus {
          outline: none;
          border-color: #4299e1;
          box-shadow: 0 0 0 2px rgba(66, 153, 225, 0.2);
        }
      }
    }
  }
}

.charts-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;

  .chart-card {
    background-color: #f7fafc;
    border-radius: 8px;
    padding: 1.25rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    border: 1px solid #e2e8f0;

    &.wide {
      grid-column: span 2;
    }

    h4 {
      font-size: 1rem;
      margin: 0 0 1rem 0;
      color: #4a5568;
      font-weight: 600;
    }

    .chart-container {
      height: 280px;
      position: relative;
    }
  }
}

/* Responsive styles for charts */
@media (max-width: 1200px) {
  .charts-grid {
    grid-template-columns: 1fr;

    .chart-card.wide {
      grid-column: auto;
    }
  }
}

@media (max-width: 768px) {
  .chart-filters {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .chart-period-controls {
    flex-wrap: wrap;
  }

  .chart-container {
    height: 220px !important;
  }
}

/* Stats Grid Styling */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.stat-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  overflow: hidden;
  position: relative;
  height: 100%;
  min-height: 160px;
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
}

.stat-card[data-type='bookings'] {
  background: linear-gradient(
    135deg,
    rgba(130, 87, 230, 0.05) 0%,
    rgba(130, 87, 230, 0.15) 100%
  );
  border-left: 4px solid #8257e6;
}

.stat-card[data-type='trips'] {
  background: linear-gradient(
    135deg,
    rgba(6, 182, 212, 0.05) 0%,
    rgba(6, 182, 212, 0.15) 100%
  );
  border-left: 4px solid #06b6d4;
}

.stat-card[data-type='revenue'] {
  background: linear-gradient(
    135deg,
    rgba(34, 197, 94, 0.05) 0%,
    rgba(34, 197, 94, 0.15) 100%
  );
  border-left: 4px solid #22c55e;
}

.stat-card-content {
  display: flex;
  padding: 1.5rem;
  height: 100%;
}

.stat-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 12px;
  margin-right: 1rem;
}

.stat-card[data-type='bookings'] .stat-icon-wrapper {
  background: rgba(130, 87, 230, 0.15);
  color: #8257e6;
}

.stat-card[data-type='trips'] .stat-icon-wrapper {
  background: rgba(6, 182, 212, 0.15);
  color: #06b6d4;
}

.stat-card[data-type='revenue'] .stat-icon-wrapper {
  background: rgba(34, 197, 94, 0.15);
  color: #22c55e;
}

.stat-icon {
  font-size: 1.5rem;
}

.stat-details {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.stat-details h3 {
  font-size: 0.95rem;
  font-weight: 500;
  color: #64748b;
  margin: 0 0 0.5rem 0;
}

.stat-value {
  font-size: 1.8rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 0.75rem 0;
}

.stat-trend {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: auto;
}

.trend-indicator {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.5rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  gap: 0.25rem;
}

.trend-up {
  background-color: rgba(34, 197, 94, 0.15);
  color: #16a34a;
}

.trend-down {
  background-color: rgba(239, 68, 68, 0.15);
  color: #dc2626;
}

.trend-neutral {
  background-color: rgba(100, 116, 139, 0.15);
  color: #64748b;
}

.stat-trend small {
  font-size: 0.75rem;
  color: #94a3b8;
}

/* Responsive design for stats grid */
@media screen and (max-width: 1024px) {
  .stats-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
  }

  .stat-card-content {
    padding: 1.25rem;
  }

  .stat-icon-wrapper {
    width: 50px;
    height: 50px;
  }

  .stat-value {
    font-size: 1.5rem;
  }
}

@media screen and (max-width: 768px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media screen and (max-width: 576px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }

  .stat-card {
    min-height: 130px;
  }

  .stat-card-content {
    padding: 1rem;
  }

  .stat-icon-wrapper {
    width: 45px;
    height: 45px;
  }

  .stat-icon {
    font-size: 1.2rem;
  }

  .stat-value {
    font-size: 1.4rem;
    margin-bottom: 0.5rem;
  }
}

/* Password protection styles */
.password-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(15, 23, 42, 0.75);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.password-prompt {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  padding: 2.5rem;
  border-radius: 16px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1);
  text-align: center;
  width: 420px;
  max-width: 90%;
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.password-prompt h2 {
  color: #1e293b;
  margin-bottom: 0.75rem;
  font-size: 1.5rem;
  font-weight: 700;
}

.password-prompt > p {
  color: #64748b;
  font-size: 0.95rem;
  margin-bottom: 1rem;
}

.password-input {
  display: flex;
  margin-top: 1.5rem;
  margin-bottom: 1rem;
  gap: 0;
}

.password-input input {
  flex: 1;
  padding: 0.875rem 1rem;
  font-size: 1rem;
  border: 2px solid #e2e8f0;
  border-right: none;
  border-radius: 8px 0 0 8px;
  background-color: #f8fafc;
  color: #1e293b;
  transition: all 0.2s ease;
}

.password-input input:focus {
  outline: none;
  border-color: #3b82f6;
  background-color: #ffffff;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.password-input input::placeholder {
  color: #94a3b8;
}

.password-input button {
  padding: 0.875rem 1.75rem;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  font-weight: 600;
  font-size: 0.95rem;
  border: none;
  border-radius: 0 8px 8px 0;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.password-input button:hover {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  transform: translateX(2px);
}

.password-input button:active {
  transform: scale(0.98);
}

.error-message {
  color: #dc2626;
  background-color: rgba(220, 38, 38, 0.08);
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-top: 1rem;
  font-size: 0.9rem;
  font-weight: 500;
  border: 1px solid rgba(220, 38, 38, 0.15);
}

/* content-blurred removed - using v-if instead */

/* Lock icon for password prompt */
.password-prompt::before {
  content: '\f023';
  font-family: 'Font Awesome 5 Free';
  font-weight: 900;
  display: block;
  font-size: 3rem;
  color: #3b82f6;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.15) 100%);
  width: 80px;
  height: 80px;
  line-height: 80px;
  border-radius: 50%;
  margin: 0 auto 1.5rem auto;
}

/* Responsive */
@media (max-width: 480px) {
  .password-prompt {
    padding: 1.75rem;
    margin: 1rem;
  }
  
  .password-input {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .password-input input {
    border-right: 2px solid #e2e8f0;
    border-radius: 8px;
  }
  
  .password-input input:focus {
    border-color: #3b82f6;
  }
  
  .password-input button {
    border-radius: 8px;
    width: 100%;
  }
}

/* ...existing chart styles... */
</style>
